<template>
  <div class="time-picker">
    <div class="field-label">
      <label>{{label}}</label>
      <div v-if="helpText" class="field-help">
        <sprite name="help" />
        <tooltip class="field-helper-text" :content="helpText" />
      </div>
    </div>

    <select class="hours" v-model="hour">
      <option class="placeholder" value="">hh</option>

      <option
        class="value"
        v-for="h in availableHours"
        :key="`h-${h}`"
        :value="h"
        :selected="h === hour">
        {{ h }}
      </option>
    </select>

    <span class="seperator">:</span>

    <select class="minutes" v-model="minute">
      <option class="placeholder" value="">mm</option>

      <option
        class="value"
        v-for="m in availableMinutes"
        :key="`m-${m}`"
        :value="m"
        :selected="m === minute">
        {{ m }}
      </option>
    </select>

    <span class="seperator">:</span>

    <select class="seconds" v-model="second">
      <option class="placeholder" value="">ss</option>

      <option
        class="value"
        v-for="s in availableSeconds"
        :key="`s-${s}`"
        :value="s"
        :selected="s === second">
        {{ s }}
      </option>
    </select>
  </div>
</template>

<script>
import input from '@/mixins/input'
import Tooltip from '@/components/Tooltip'
import Sprite from '@/components/Sprite'

/**
 * pad a number with leading zeros to make it a required length
 *
 * @param  {number} number - number to pad
 * @param  {number} digits - length of ouput string
 * @returns {string} `number` padded with leading zeros to length `didgets`
 */
function padDidgets(number, digits) {
  return Array(Math.max(digits - String(number).length + 1, 0)).join(0) + number
}

const TOTAL_HOURS = 24
const TOTAL_MINUTES = 60
const TOTAL_SECONDS = 60

export default {
  name: 'TimePicker',

  mixins: [input],

  props: {
    label: {
      type: String
    },

    helpText: {
      type: String
    }
  },

  components: {
    Tooltip,
    Sprite
  },

  computed: {
    hasValue() {
      return this.hour && this.minute && this.second
    },

    currentValue: {
      get() {
        if (!this.resource) {
          return ''
        }
        return this.resource.split(':')
      },

      set(newValue) {
        this.$emit('input', newValue.join(':'))
      }
    },

    second: {
      get() {
        return this.currentValue[2]
      },

      set(newSecond) {
        let value = this.currentValue
        this.currentValue = [value[0], value[1], newSecond]
      }
    },

    minute: {
      get() {
        return this.currentValue[1]
      },

      set(newMinute) {
        let value = this.currentValue
        this.currentValue = [value[0], newMinute, value[2]]
      }
    },

    hour: {
      get() {
        return this.currentValue[0]
      },

      set(newHour) {
        let value = this.currentValue
        this.currentValue = [newHour, value[1], value[2]]
      }
    },

    availableHours() {
      return new Array(TOTAL_HOURS)
        .fill()
        .map((_d, i) => padDidgets(i + 0, 2))
    },

    availableMinutes() {
      return new Array(TOTAL_MINUTES)
        .fill()
        .map((_d, i) => padDidgets(i + 1, 2))
    },

    availableSeconds() {
      return new Array(TOTAL_SECONDS)
        .fill()
        .map((_d, i) => padDidgets(i + 0, 2))
    }
  }
}
</script>

<style lang="sass">
  .time-picker
    .seperator
      margin: 0 3px
</style>
